const ONETRUST_SCRIPT_URL = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
const ONETRUST_DOMAIN_SCRIPTS = {
  production: '01998236-c606-7497-824f-2d3e86fd5c68',
  staging: '01998236-c606-7497-824f-2d3e86fd5c68-test',
} as const

/** OneTrust Client-Side Cookie Management
 * @see https://my.onetrust.com/s/article/UUID-518074a1-a6da-81c3-be52-bae7685d9c94 for relevant docs
 */

export const ONETRUST_CATEGORIES = {
  STRICTLY_NECESSARY: 'C0001',
  PERFORMANCE: 'C0002',
  FUNCTIONAL: 'C0003',
  TARGETING: 'C0004',
  SOCIAL_MEDIA: 'C0005',
} as const

export type OneTrustCategory = (typeof ONETRUST_CATEGORIES)[keyof typeof ONETRUST_CATEGORIES]

declare global {
  interface Window {
    OnetrustActiveGroups?: string
    OneTrust?: {
      OnConsentChanged: (callback: (event: { detail: string[] }) => void) => void
    }
    OptanonWrapper?: () => void
  }
}

/**
 * Checks if OneTrust is intended to be active on the page.
 * Checks for the OneTrust script tag rather than the SDK object (which loads async).
 * If OneTrust is not active (e.g., feature flag disabled), consent gating should be bypassed.
 * @returns true if OneTrust script tag is present in the DOM
 */
export function isOneTrustActive(): boolean {
  if (typeof document === 'undefined') return false

  const oneTrustScript = document.querySelector('script[data-domain-script]')
  const isActive = oneTrustScript !== null
  return isActive
}

/**
 * Checks if user has consented to a specific cookie category.
 * If OneTrust is not loaded, returns true (allow all - no consent gating).
 * @param category - The OneTrust category ID to check (e.g., 'C0002')
 * @returns true if consent is granted or OneTrust is not active
 */
export function hasConsentForCategory(category: OneTrustCategory): boolean {
  // If OneTrust is not loaded, bypass consent gating
  if (!isOneTrustActive()) {
    return true
  }

  const activeGroups = window.OnetrustActiveGroups ?? ''
  const hasConsent = activeGroups.includes(category)
  return hasConsent
}

/**
 * Checks if user has consented to Functional cookies (C0003)
 */
export function hasFunctionalConsent() {
  return hasConsentForCategory(ONETRUST_CATEGORIES.FUNCTIONAL)
}

/**
 * Checks if user has consented to Performance cookies (C0002)
 */
export function hasPerformanceConsent() {
  return hasConsentForCategory(ONETRUST_CATEGORIES.PERFORMANCE)
}

/**
 * Checks if user has consented to Targeting cookies (C0004)
 */
export function hasTargetingConsent() {
  return hasConsentForCategory(ONETRUST_CATEGORIES.TARGETING)
}

export type ConsentChangeCallback = () => void

/**
 * Sets up a listener for OneTrust consent changes.
 * Listens for:
 * - OneTrustGroupsUpdated: fires when user updates consent preferences
 * - OTConsentApplied: fires when consent is applied (including on page load)
 * @param callback - Function to call when consent changes
 */
export function onConsentChange(callback: ConsentChangeCallback) {
  if (typeof window === 'undefined') return

  // OneTrust native event: fires when user updates consent in the preference center
  window.addEventListener('OneTrustGroupsUpdated', () => {
    callback()
  })

  // OneTrust native event: fires when consent is applied (page load or user action)
  window.addEventListener('OTConsentApplied', () => {
    callback()
  })
}

export interface OneTrustConfig {
  enabled: boolean
  environment: 'development' | 'staging' | 'production'
  domainScript: string
  scriptSrc: string
}

export type SkipReason = 'already-loaded' | 'disabled' | null

export class OneTrustService {
  private isLoaded = false
  private skipReason: SkipReason = null

  getSkipReason(): SkipReason {
    return this.skipReason
  }

  getIsLoaded(): boolean {
    return this.isLoaded
  }

  private getConfig(): OneTrustConfig {
    const hostname = window.location.hostname
    const config: OneTrustConfig = {
      enabled: false,
      environment: 'development',
      domainScript: '',
      scriptSrc: ONETRUST_SCRIPT_URL,
    }

    if (hostname.includes('auth.closingfolders.com')) {
      config.enabled = true
      config.environment = 'production'
      config.domainScript = ONETRUST_DOMAIN_SCRIPTS.production
    } else if (hostname.includes('auth.staging.closingfolders.com')) {
      config.enabled = true
      config.environment = 'staging'
      config.domainScript = ONETRUST_DOMAIN_SCRIPTS.staging
    }

    return config
  }

  private injectScripts(config: OneTrustConfig): void {
    const startComment = document.createComment(
      ` OneTrust Cookies Consent Notice start in ${config.environment} `,
    )
    document.head.insertBefore(startComment, document.head.firstChild)

    const oneTrustScript = document.createElement('script')
    oneTrustScript.src = config.scriptSrc
    oneTrustScript.type = 'text/javascript'
    oneTrustScript.setAttribute('data-domain-script', config.domainScript)
    document.head.insertBefore(oneTrustScript, startComment.nextSibling)

    const wrapperScript = document.createElement('script')
    wrapperScript.type = 'text/javascript'
    wrapperScript.textContent = 'function OptanonWrapper() { }'
    document.head.insertBefore(wrapperScript, oneTrustScript.nextSibling)

    const endComment = document.createComment(
      ` OneTrust Cookies Consent Notice end in ${config.environment} `,
    )
    document.head.insertBefore(endComment, wrapperScript.nextSibling)
  }

  init(): void {
    if (this.isLoaded) {
      this.skipReason = 'already-loaded'
      return
    }

    const config = this.getConfig()

    if (config.enabled) {
      this.injectScripts(config)
      this.isLoaded = true
    } else {
      this.skipReason = 'disabled'

      const disabledComment = document.createComment(` OneTrust disabled in ${config.environment} `)
      document.head.insertBefore(disabledComment, document.head.firstChild)
    }
  }

  reset(): void {
    this.isLoaded = false
    this.skipReason = null
  }
}

export const oneTrust = new OneTrustService()
